import { css, cva } from '@styled-system/css';
import { ArrowUpRight, Globe, Star, Users } from 'lucide-react';
import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useParams } from 'react-router-dom';

import { useAnalysisSegment } from '@/features/analysisPage/contexts/AnalysisSegmentContext';
import type { AnalysisPlatform, AnalysisSellerRegion } from '@/shared/api/types/generated';
import { Avatar } from '@/shared/components/ui/Avatar';
import { Select } from '@/shared/components/ui/Select';
import { Spinner } from '@/shared/components/ui/Spinner';
import { useGetAnalysisTopSellersQuery } from '@/shared/hooks/queries/generated';
import { formatRevenue } from '@/shared/utils/analysis/nicheScore';

// Forme d'affichage d'un vendeur (issue de getAnalysisTopSellers)
interface SellerData {
  id: string;
  name: string;
  avatarUrl: string | null;
  /** null sur VC quand l'API ne fournit pas encore de lien (pas de repli construit côté front). */
  profileUrl: string | null;
  region: AnalysisSellerRegion | null;
  rating: number | null;
  reviews: number | null;
  monthlyRevenue: number | null;
  monthlySales: number | null;
  avgPrice: number | null;
  /** VC uniquement : "Expert" | "Trusted" | "Common" (texte brut VC). null sur Vinted. */
  ratingBadge: string | null;
  /** VC uniquement : ventes du vendeur DANS l'analyse (critère de classement). null sur Vinted. */
  salesInAnalysis: number | null;
  lastSales: { imageUrl: string | null; title: string; price: number | null }[];
}

// Sélecteur de région : Europe de l'Ouest par défaut (recommandation back —
// un profil hors zone peut ne rien afficher à l'ouverture), 'all' = pas de
// filtre serveur.
const REGION_VALUES = ['europeWest', 'unitedKingdom', 'europeEast', 'northAmerica', 'others'] as const;
type RegionFilterValue = (typeof REGION_VALUES)[number] | 'all';

// ============================================================================
// Onglet Vendeurs — les vendeurs récurrents du périmètre : qui vit de cette
// niche, à quel volume et à quel prix. Data mock en attendant l'agrégat
// vendeur du back v2 (calculable depuis sellerUrl/soldAt).
// ============================================================================

const container = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '5',
  paddingTop: '3',
});

// ─── Tuile de la donnée principale (même habillage que les stats clés) ─────

const statTile = css({
  display: 'flex',
  alignItems: 'center',
  gap: '5',
  paddingY: '5',
  paddingX: '6',
  bg: 'surface.subtle',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  borderRadius: 'lg',
  width: 'fit-content',
  minWidth: '240px',
});

const statIcon = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '36px',
  height: '36px',
  borderRadius: 'md',
  bg: 'surface.sunken',
  color: 'text.secondary',
  flexShrink: 0,
});

const statBody = css({ display: 'flex', flexDirection: 'column', gap: '0.5' });

const statValue = css({
  fontFamily: 'mono',
  fontSize: '18px',
  lineHeight: '1.15',
  fontWeight: 'bold',
  color: 'text.primary',
  letterSpacing: '-0.5px',
});

const statLabel = css({ fontSize: 'caption', color: 'text.tertiary' });

// ─── Cartes vendeur ────────────────────────────────────────────────────────

const list = css({ display: 'flex', flexDirection: 'column', gap: '3' });

const sellerCard = cva({
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '6',
    paddingY: '4',
    paddingX: '6',
    bg: 'surface.default',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'border.default',
    borderRadius: 'xl',
    transition: 'border-color 120ms ease, background 120ms ease',
    flexWrap: 'wrap',
  },
  variants: {
    interactive: {
      true: {
        cursor: 'pointer',
        _hover: {
          borderColor: 'border.strong',
          bg: 'surface.subtle',
          '& [data-external-hint]': { opacity: 1 },
        },
      },
      false: {},
    },
  },
  defaultVariants: { interactive: true },
});

const identity = css({
  display: 'flex',
  alignItems: 'center',
  gap: '4',
  flex: 1,
  minWidth: '200px',
});

const identityText = css({ display: 'flex', flexDirection: 'column', gap: '0.5', minWidth: 0 });

const sellerName = css({
  fontSize: 'bodySm',
  fontWeight: 'semibold',
  color: 'text.primary',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const sellerMeta = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '1.5',
  fontSize: 'caption',
  color: 'text.tertiary',
});

const regionBadge = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '1',
  paddingY: '0.5',
  paddingX: '2.5',
  borderRadius: 'full',
  fontSize: 'micro',
  fontWeight: 'medium',
  bg: 'surface.sunken',
  color: 'text.tertiary',
  whiteSpace: 'nowrap',
  flexShrink: 0,
});

// Grille à colonnes FIXES : les valeurs nulles ou courtes ne décalent rien
const metrics = css({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 110px)',
  alignItems: 'center',
  gap: '6',
  flexShrink: 0,
});

const metricBlock = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5',
  minWidth: 0,
});

const metricValue = css({
  fontFamily: 'mono',
  fontSize: 'bodySm',
  fontWeight: 'bold',
  color: 'text.primary',
  letterSpacing: '-0.3px',
  whiteSpace: 'nowrap',
});

const metricLabel = css({ fontSize: 'caption', color: 'text.tertiary', whiteSpace: 'nowrap' });

// Pile des 5 dernières ventes — vignettes empilées avec léger décalage,
// la plus récente au-dessus.
// Largeur réservée (4 vignettes max) même quand la pile est vide → alignement
const salesStack = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexShrink: 0,
  width: '112px',
  paddingRight: '2',
});

const stackThumb = css({
  width: '40px',
  height: '52px',
  borderRadius: 'md',
  objectFit: 'cover',
  bg: 'surface.sunken',
  borderWidth: '2px',
  borderStyle: 'solid',
  borderColor: 'surface.default',
  boxShadow: 'xs',
  marginLeft: '-18px',
  transition: 'transform 120ms ease',
  _first: { marginLeft: '0' },
  _hover: { transform: 'translateY(-4px)' },
});

const externalHint = css({
  display: 'inline-flex',
  alignItems: 'center',
  color: 'text.tertiary',
  opacity: 0,
  transition: 'opacity 120ms ease',
  flexShrink: 0,
});

// ============================================================================

const SellerRow: FC<{ seller: SellerData; lang: string; platform: AnalysisPlatform }> = ({
  seller,
  lang,
  platform,
}) => {
  const { t } = useTranslation();
  const profileUrl = seller.profileUrl;

  const openProfile = () => {
    if (profileUrl) window.open(profileUrl, '_blank', 'noopener,noreferrer');
  };

  const openProfileLabel = platform === 'vestiaire' ? t('sellers.openProfileVestiaire') : t('sellers.openProfile');

  return (
    <div
      className={sellerCard({ interactive: !!profileUrl })}
      role={profileUrl ? 'link' : undefined}
      tabIndex={profileUrl ? 0 : undefined}
      onClick={profileUrl ? openProfile : undefined}
      onKeyDown={
        profileUrl
          ? (event) => {
              if (event.key === 'Enter') openProfile();
            }
          : undefined
      }
      aria-label={profileUrl ? openProfileLabel : undefined}
    >
      <div className={identity}>
        <Avatar name={seller.name} src={seller.avatarUrl ?? undefined} size="lg" />
        <div className={identityText}>
          <span className={css({ display: 'inline-flex', alignItems: 'center', gap: '2', minWidth: 0 })}>
            <span className={sellerName}>@{seller.name}</span>
            {seller.region && (
              <span className={regionBadge}>
                <Globe size={9} strokeWidth={1.75} />
                {t(`sellers.regions.${seller.region}`)}
              </span>
            )}
          </span>
          <span className={sellerMeta}>
            {platform === 'vestiaire' ? (
              <>
                {seller.ratingBadge
                  ? t(`sellers.ratingBadge.${seller.ratingBadge.toLowerCase()}`, {
                      defaultValue: seller.ratingBadge,
                    })
                  : '—'}
                {' · '}
                {t('sellers.salesInAnalysis', { count: seller.salesInAnalysis ?? 0 })}
              </>
            ) : (
              <>
                <Star size={11} strokeWidth={1.75} fill="currentColor" />
                {seller.rating == null
                  ? '—'
                  : `${(seller.rating * 5).toLocaleString(lang.startsWith('en') ? 'en-GB' : 'fr-FR', {
                      maximumFractionDigits: 1,
                    })}/5`}
                {' · '}
                {t('salesFeed.reviews', { count: seller.reviews ?? 0 })}
              </>
            )}
          </span>
        </div>
      </div>

      <div className={metrics}>
        <div className={metricBlock}>
          <span className={metricValue}>{formatRevenue(seller.monthlyRevenue, lang)}</span>
          <span className={metricLabel}>{t('sellers.revenuePerMonth')}</span>
        </div>
        <div className={metricBlock}>
          <span className={metricValue}>{seller.monthlySales ?? '—'}</span>
          <span className={metricLabel}>{t('sellers.salesPerMonth')}</span>
        </div>
        <div className={metricBlock}>
          <span className={metricValue}>
            {seller.avgPrice == null
              ? '—'
              : `${seller.avgPrice.toLocaleString(lang.startsWith('en') ? 'en-GB' : 'fr-FR', {
                  maximumFractionDigits: 1,
                })} €`}
          </span>
          <span className={metricLabel}>{t('sellers.avgPrice')}</span>
        </div>
      </div>

      {/* Aperçu empilé des 5 dernières ventes */}
      <div className={salesStack} aria-label={t('sellers.lastSales')}>
        {seller.lastSales
          .filter((sale) => sale.imageUrl)
          .map((sale, index, list) => (
            <img
              key={index}
              src={sale.imageUrl ?? undefined}
              alt={sale.title}
              title={sale.price == null ? sale.title : `${sale.title} · ${sale.price} €`}
              className={stackThumb}
              style={{ zIndex: list.length - index }}
              loading="lazy"
            />
          ))}
      </div>

      {profileUrl && (
        <span data-external-hint className={externalHint}>
          <ArrowUpRight size={15} strokeWidth={1.75} />
        </span>
      )}
    </div>
  );
};

export interface AnalysisPageSellersProps {
  /** 'vendor' : la liste devient les vendeurs SIMILAIRES au profil analysé. */
  variant?: 'product' | 'vendor';
}

export const AnalysisPageSellers: FC<AnalysisPageSellersProps> = ({ variant = 'product' }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language ?? 'fr';
  const { id: analysisId = '' } = useParams<{ id: string }>();
  const { platform } = useAnalysisSegment();
  const [regionFilter, setRegionFilter] = useState<RegionFilterValue>('europeWest');

  // Top vendeurs réels — non segmenté par design (cf changelog seller-page),
  // snapshot figé à la création → jamais périmé. Filtre régional serveur :
  // les vendeurs à région inconnue sont exclus dès qu'un filtre est passé.
  const { data, isLoading } = useGetAnalysisTopSellersQuery(
    { analysisId, regions: regionFilter === 'all' ? undefined : [regionFilter] },
    { enabled: !!analysisId, staleTime: Infinity, gcTime: 30 * 60 * 1000 },
  );

  const sellers = useMemo((): SellerData[] => {
    const rows = data?.getAnalysisTopSellers.sellers ?? [];
    return rows.map((seller) => ({
      id: seller.sellerId,
      name: seller.username ?? t('sellers.usernameFallback'),
      avatarUrl: seller.imageUrl ?? null,
      // L'API rend désormais profileUrl sur Vinted ET VC ; repli Vinted seul pour un
      // cache ancien (VC sans repli possible : l'id ne se résout pas côté front).
      profileUrl: seller.profileUrl ?? (platform === 'vestiaire' ? null : `https://www.vinted.fr/member/${seller.sellerId}`),
      region: seller.region ?? null,
      rating: seller.rating ?? null,
      reviews: seller.reviews ?? null,
      monthlyRevenue: seller.revenuePerMonth ?? null,
      monthlySales: seller.salesPerMonth ?? null,
      avgPrice: seller.avgPrice ?? null,
      ratingBadge: seller.ratingBadge ?? null,
      salesInAnalysis: seller.salesInAnalysis ?? null,
      lastSales: seller.lastSales.map((sale) => ({
        imageUrl: sale.imageUrl ?? null,
        title: sale.title,
        price: sale.price ?? null,
      })),
    }));
  }, [data, t, platform]);

  const recurringCount = data?.getAnalysisTopSellers.recurringSellerCount ?? 0;

  if (isLoading) {
    return (
      <div className={css({ display: 'flex', justifyContent: 'center', paddingY: '16' })}>
        <Spinner size={24} />
      </div>
    );
  }

  return (
    <div className={container}>
      {/* Donnée principale : vendeurs récurrents de la niche, ou vendeurs
          similaires au profil analysé. Le compteur récurrents n'est pas
          affecté par le filtre régional (comptage sur tout le snapshot). */}
      <div className={css({ display: 'flex', alignItems: 'center', gap: '4', flexWrap: 'wrap' })}>
        <div className={statTile}>
          <span className={statIcon}>
            <Users size={17} strokeWidth={1.75} />
          </span>
          <div className={statBody}>
            <span className={statValue}>{variant === 'vendor' ? sellers.length : recurringCount}</span>
            <span className={statLabel}>{variant === 'vendor' ? t('sellers.similar') : t('sellers.recurring')}</span>
          </div>
        </div>

        <div className={css({ marginLeft: 'auto' })}>
          <Select
            value={regionFilter}
            onChange={(value) => setRegionFilter(value as RegionFilterValue)}
            options={[
              ...REGION_VALUES.map((region) => ({ value: region, label: t(`sellers.regions.${region}`) })),
              { value: 'all', label: t('sellers.regions.all') },
            ]}
            width="200px"
            size="sm"
          />
        </div>
      </div>

      <div className={list}>
        {sellers.map((seller) => (
          <SellerRow key={seller.id} seller={seller} lang={lang} platform={platform} />
        ))}
      </div>
    </div>
  );
};
